import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

const SITE_URL = 'https://www.artemcreations.com';
const SITE_NAME = 'ArtemCréations';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;
const DEFAULT_IMAGE_ALT = 'ArtemCréations — sacs crochet faits main, pièces uniques artisanales';

/**
 * Build an absolute URL from a possibly relative one.
 * Ensures og:image is always absolute (required by Facebook/WhatsApp/X crawlers).
 */
const absoluteUrl = (maybeUrl) => {
  if (!maybeUrl) return DEFAULT_IMAGE;
  if (/^https?:\/\//i.test(maybeUrl)) return maybeUrl;
  if (maybeUrl.startsWith('/')) return `${SITE_URL}${maybeUrl}`;
  return `${SITE_URL}/${maybeUrl}`;
};

const SEO = ({
  title,
  description,
  image,
  imageAlt,
  imageWidth = 1200,
  imageHeight = 630,
  type = 'website',
  price,
  currency = 'EUR',
  noindex = false,
  jsonLd,
}) => {
  const location = useLocation();
  const url = `${SITE_URL}${location.pathname}`;
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Sacs Crochet Fait Main | Pièces Uniques Artisanales`;
  const ogImage = absoluteUrl(image);
  const ogImageAlt = imageAlt || DEFAULT_IMAGE_ALT;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      <link rel="alternate" hrefLang="fr" href={url} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:secure_url" content={ogImage} />
      <meta property="og:image:width" content={String(imageWidth)} />
      <meta property="og:image:height" content={String(imageHeight)} />
      <meta property="og:image:alt" content={ogImageAlt} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="fr_FR" />
      {price && <meta property="product:price:amount" content={price} />}
      {price && <meta property="product:price:currency" content={currency} />}
      {price && <meta property="og:price:amount" content={price} />}
      {price && <meta property="og:price:currency" content={currency} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={ogImageAlt} />

      {/* JSON-LD */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
